import {
  inject,
  Injector,
  LOCALE_ID,
  NgModule,
  PLATFORM_ID,
  provideAppInitializer,
} from '@angular/core';
import {
  BrowserModule,
  HammerModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CoreModule } from '@core/core.module';
import { ClipboardModule } from '@angular/cdk/clipboard';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { LanguageLoader } from '@shared/language/language.service';
import { MathTranslateMessageFormatCompiler } from '@shared/language/translate-message-format.compiler';
import {
  en_US,
  NZ_DATE_CONFIG,
  NZ_I18N,
  pl_PL,
  uk_UA,
} from 'ng-zorro-antd/i18n';
import helpCrunchWidgetFactory from '@shared/widgets/help-crunch/help-crunch-widget-factory';
import { LocalStorageKey } from '@shared/services/local-storage.service';
import { NzIconsModule } from './shared/modules/nz-icons.module';
import { LuxonLocaleService } from '@core/services/luxon-locale.service';
import { NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { ngZorroConfig } from './shared/constants';
import { DatePipeLocaleService } from '@core/services/date-pipe-locale.service';
import { getLocaleId } from '@shared/utils';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ClarityService } from './shared/marketing-management/clarity.service';
import { PosthogService } from '@shared/services/posthog.service';
import { MarketingTrackingService } from '@shared/marketing-management/marketing-tracking.service';
import { ThemeService } from '@shared/theme/theme.service';
import { RealtimeService } from '@shared/services/realtime.service';
import { MathNotificationToastComponent } from '@shared/components/math-notification-toast/math-notification-toast.component';
import { AppUpdateToastComponent } from '@shared/components/app-update-toast/app-update-toast.component';
import { RouterOutlet } from '@angular/router';
import { LoginLinkService } from '@shared/services/login-link/login-link.service';
import { AuthTabSyncService } from '@core/services/auth-tab-sync/auth-tab-sync.service';

export function initializeMarketingTracking(
  marketingTrackingService: MarketingTrackingService,
): () => void {
  return () => {
    marketingTrackingService.init();
  };
}

// Factory function to initialize Luxon locale
export function initializeLuxonLocale(
  luxonService: LuxonLocaleService,
): () => void {
  return () => {
    luxonService.initializeLuxonLocale();
  };
}

// Factory function to initialize Angular DatePipe locale
export function initializeDatePipeLocale(
  datePipeLocaleService: DatePipeLocaleService,
): () => void {
  return () => {
    datePipeLocaleService.initializeLocale();
  };
}

export function initializeClarity(clarityService: ClarityService): () => void {
  return () => {
    clarityService.init();
  };
}

export function initializePosthog(posthogService: PosthogService): () => void {
  return () => {
    posthogService.init();
  };
}

export function initializeRealtime(
  realtimeService: RealtimeService,
): () => void {
  return () => {
    realtimeService.init();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: LanguageLoader,
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: MathTranslateMessageFormatCompiler,
      },
    }),
    AppRoutingModule,
    CommonModule,
    BrowserAnimationsModule,
    ScrollingModule,
    CoreModule,
    HammerModule,
    ClipboardModule,
    NzIconsModule,
    MathNotificationToastComponent,
    AppUpdateToastComponent,
    RouterOutlet,
  ],
  providers: [
    provideClientHydration(withEventReplay()),
    provideAppInitializer(() => {
      const initializerFn = initializeMarketingTracking(
        inject(MarketingTrackingService),
      );
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useFactory: getLocaleId,
    },
    {
      provide: NZ_I18N,
      useFactory: () => {
        const platformId = inject(PLATFORM_ID);
        let userLanguage: string | null = null;

        if (isPlatformBrowser(platformId)) {
          userLanguage = localStorage?.getItem(LocalStorageKey.UI_LANGUAGE);
        }

        switch (userLanguage) {
          case 'en':
            return en_US;
          case 'pl':
            return pl_PL;
          case 'ua':
            return uk_UA;
          default:
            return en_US;
        }
      },
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideAppInitializer(() => {
      const initializerFn = helpCrunchWidgetFactory(inject(Injector));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeLuxonLocale(inject(LuxonLocaleService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeDatePipeLocale(
        inject(DatePipeLocaleService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeClarity(inject(ClarityService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializePosthog(inject(PosthogService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (
        (themeService: ThemeService) => () =>
          themeService.init()
      )(inject(ThemeService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeRealtime(inject(RealtimeService));
      return initializerFn();
    }),
    provideAppInitializer(() => inject(LoginLinkService).init()),
    provideAppInitializer(() => inject(AuthTabSyncService).init()),
    {
      provide: NZ_DATE_CONFIG,
      useValue: {
        firstDayOfWeek: 1,
      },
    },
    {
      provide: NZ_CONFIG,
      useValue: ngZorroConfig,
    },
    NzModalService,
    NzDrawerService,
  ],
})
export class AppModule {}
